/**
 * Profile Page
 * User profile and personal settings
 */

import { useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useNotification } from '@/hooks/useNotification';
import { Card } from '@/components/common/Card';
import { Button } from '@/components/common/Button';
import { Input } from '@/components/common/Input';
import { apiRequest } from '@/api/client';

export const ProfilePage = () => {
  const { user, updateUser } = useAuth();
  const { showNotification } = useNotification();
  
  const [isEditingInfo, setIsEditingInfo] = useState(false);
  const [isEditingPassword, setIsEditingPassword] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  
  const [profileData, setProfileData] = useState({
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    email: user?.email || '',
  });
  
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  
  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleProfileChange = (field: string, value: string) => {
    setProfileData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handlePasswordChange = (field: string, value: string) => {
    setPasswordData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateProfile = () => {
    const newErrors: Record<string, string> = {};
    
    if (!profileData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }
    
    if (!profileData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }
    
    if (!profileData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(profileData.email)) {
      newErrors.email = 'Invalid email format';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validatePassword = () => {
    const newErrors: Record<string, string> = {};
    
    if (!passwordData.currentPassword) {
      newErrors.currentPassword = 'Current password is required';
    }
    
    if (!passwordData.newPassword) {
      newErrors.newPassword = 'New password is required';
    } else if (passwordData.newPassword.length < 8) {
      newErrors.newPassword = 'Password must be at least 8 characters';
    }
    
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSaveProfile = async () => {
    if (!validateProfile()) return;
    
    setIsSaving(true);
    try {
      const response = await apiRequest('update_teacher_profile', {
        teacherId: user?.teacherId,
        email: user?.email,
        firstName: profileData.firstName,
        lastName: profileData.lastName,
        newEmail: profileData.email !== user?.email ? profileData.email : undefined,
      });
      
      if (response.success) {
        // Update local user data
        updateUser({
          ...user!,
          firstName: profileData.firstName,
          lastName: profileData.lastName,
          email: profileData.email,
        });
        
        showNotification('success', 'Profile updated successfully');
        setIsEditingInfo(false);
      } else {
        showNotification('error', (response as any).message || 'Failed to update profile');
      }
    } catch (error) {
      console.error('Failed to update profile:', error);
      showNotification('error', 'Failed to update profile');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSavePassword = async () => {
    if (!validatePassword()) return;
    
    setIsSaving(true);
    try {
      const response = await apiRequest('update_teacher_password', {
        email: user?.email,
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword,
      });
      
      if (response.success) {
        showNotification('success', 'Password updated successfully');
        setIsEditingPassword(false);
        setPasswordData({
          currentPassword: '',
          newPassword: '',
          confirmPassword: '',
        });
      } else {
        showNotification('error', (response as any).message || 'Failed to update password');
      }
    } catch (error) {
      console.error('Failed to update password:', error);
      showNotification('error', 'Failed to update password');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setIsEditingInfo(false);
    setProfileData({
      firstName: user?.firstName || '',
      lastName: user?.lastName || '',
      email: user?.email || '',
    });
    setErrors({});
  };

  const handleCancelPassword = () => {
    setIsEditingPassword(false);
    setPasswordData({
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    });
    setErrors({});
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-slate-900">My Profile</h1>
        <p className="text-slate-600 mt-2">Manage your personal information and password</p>
      </div>

      {/* Personal Information */}
      <Card title="Personal Information">
        <div className="p-6 space-y-4">
          {!isEditingInfo ? (
            <>
              <div>
                <label className="text-sm font-medium text-slate-600">First Name</label>
                <p className="text-lg text-slate-900">{user?.firstName}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-600">Last Name</label>
                <p className="text-lg text-slate-900">{user?.lastName}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-600">Email</label>
                <p className="text-lg text-slate-900">{user?.email}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-600">School</label>
                <p className="text-lg text-slate-900">{user?.schoolName}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-600">Role</label>
                <p className="text-lg text-slate-900">{user?.isAdmin ? 'Administrator' : 'Teacher'}</p>
              </div>
              <div className="pt-4">
                <Button variant="primary" onClick={() => setIsEditingInfo(true)}>
                  Edit Information
                </Button>
              </div>
            </>
          ) : (
            <>
              <Input
                label="First Name"
                type="text"
                value={profileData.firstName}
                onChange={(e) => handleProfileChange('firstName', e.target.value)}
                error={errors.firstName}
                required
              />
              <Input
                label="Last Name"
                type="text"
                value={profileData.lastName}
                onChange={(e) => handleProfileChange('lastName', e.target.value)}
                error={errors.lastName}
                required
              />
              <Input
                label="Email"
                type="email"
                value={profileData.email}
                onChange={(e) => handleProfileChange('email', e.target.value)}
                error={errors.email}
                required
              />
              <p className="text-sm text-slate-500">
                Note: Changing your email will require you to log in with the new email address.
              </p>
              <div className="flex gap-3 pt-4">
                <Button
                  variant="primary"
                  onClick={handleSaveProfile}
                  disabled={isSaving}
                >
                  {isSaving ? 'Saving...' : 'Save Changes'}
                </Button>
                <Button
                  variant="outline"
                  onClick={handleCancelEdit}
                  disabled={isSaving}
                >
                  Cancel
                </Button>
              </div>
            </>
          )}
        </div>
      </Card>

      {/* Change Password */}
      <Card title="Change Password">
        <div className="p-6 space-y-4">
          {!isEditingPassword ? (
            <>
              <p className="text-slate-600">
                Update your password to keep your account secure.
              </p>
              <Button variant="primary" onClick={() => setIsEditingPassword(true)}>
                Change Password
              </Button>
            </>
          ) : (
            <>
              <Input
                label="Current Password"
                type="password"
                value={passwordData.currentPassword}
                onChange={(e) => handlePasswordChange('currentPassword', e.target.value)}
                error={errors.currentPassword}
                required
              />
              <Input
                label="New Password"
                type="password"
                value={passwordData.newPassword}
                onChange={(e) => handlePasswordChange('newPassword', e.target.value)}
                error={errors.newPassword}
                required
              />
              <Input
                label="Confirm New Password"
                type="password"
                value={passwordData.confirmPassword}
                onChange={(e) => handlePasswordChange('confirmPassword', e.target.value)}
                error={errors.confirmPassword}
                required
              />
              <p className="text-sm text-slate-500">
                Password must be at least 8 characters long.
              </p>
              <div className="flex gap-3 pt-4">
                <Button
                  variant="primary"
                  onClick={handleSavePassword}
                  disabled={isSaving}
                >
                  {isSaving ? 'Updating...' : 'Update Password'}
                </Button>
                <Button
                  variant="outline"
                  onClick={handleCancelPassword}
                  disabled={isSaving}
                >
                  Cancel
                </Button>
              </div>
            </>
          )}
        </div>
      </Card>
    </div>
  );
};
